import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  bootstrapNativeSession,
  performAccountDeletionSignOut,
  performLogoutSequence,
} from "@/lib/mobile/authOrchestration";
import { clearAccountScopedState } from "@/lib/sessionIsolation";

const AuthContext = createContext(null);

/**
 * Auth + capability state.
 *
 * States:
 *   - loading === true: initial /me probe in flight, don't render app or login
 *   - user is null: not signed in — show <LoginPage />
 *   - user is object: signed in — render the app
 *
 * Capability checks are done via `hasCapability("some:cap")`. Superuser holds
 * the wildcard "*" which grants everything.
 */
export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [providers, setProviders] = useState([]);
  const [signedOut, setSignedOut] = useState(false);
  // Capture during render, BEFORE the child's callback effect strips the hash.
  const returnedFromGoogle = useRef(typeof window !== "undefined" && window.location.hash.includes("session_id="));
  const [googleCallbackPending, setGoogleCallbackPending] = useState(returnedFromGoogle.current);
  // Native Microsoft return: `cruisecommand://auth-callback?code=…&state=…`.
  // Exposed only so AuthGate re-renders and re-evaluates hasPendingMicrosoftRedirect().
  const [microsoftCallbackPending, setMicrosoftCallbackPending] = useState(false);
  // Native OAuth return: the deep link arrives AFTER mount (NativeBridge writes
  // `#session_id=…` / `#code=…&state=…` to the hash). React does not re-render
  // on a bare hash assignment, so observe `hashchange` and flip the flags.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onHashChange = () => {
      const h = window.location.hash || "";
      if (h.includes("session_id=")) setGoogleCallbackPending(true);
      if (h.includes("code=") && h.includes("state=")) setMicrosoftCallbackPending(true);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  const authEpoch = useRef(0);
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const refreshMe = useCallback(async () => {
    const epoch = authEpoch.current;
    try {
      const { data } = await api.get("/api/auth/me");
      if (epoch !== authEpoch.current) return null;
      if (userRef.current?.id && userRef.current.id !== data?.id) clearAccountScopedState();
      setUser(data);
      setGoogleCallbackPending(false);
      setMicrosoftCallbackPending(false);
      setSignedOut(false);
      try {
        if (data && typeof window !== "undefined" && window.dispatchEvent) {
          window.dispatchEvent(new Event("cc:auth_ready"));
        }
      } catch { /* non-DOM env */ }
      return data;
    } catch (error) {
      if (epoch !== authEpoch.current) return null;
      if ([401, 403].includes(error?.response?.status)) clearAccountScopedState();
      setUser(null);
      return null;
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/providers", { _skipSessionTracking: true });
      setProviders(data.providers || []);
    } catch {
      setProviders([]);
    }
  }, []);

  useEffect(() => {
    // PD-028 · Skip /me probe if returning from Emergent Google Auth.
    if (returnedFromGoogle.current) {
      setLoading(false);
      loadProviders();
      return;
    }
    (async () => {
      // RC-MOBILE-AUTH-REPAIR-001 Stage 1c · Native boot gate. On web
      // this resolves immediately (no-op). On native it runs the
      // install-identity check (clearing restored Keychain tokens on a
      // fresh install) and attaches the Authorization: Bearer +
      // single-flight interceptors BEFORE the /me probe below runs, so
      // the probe either presents a valid Bearer or fails cleanly.
      await bootstrapNativeSession();
      await Promise.all([refreshMe(), loadProviders()]);
      setLoading(false);
    })();
  }, [refreshMe, loadProviders]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    setUser(data);
    setSignedOut(false);
    return data;
  }, []);

  const applySignedOut = useCallback((reason, broadcast = true) => {
    authEpoch.current += 1;
    clearAccountScopedState();
    setUser(null);
    setSignedOut(true);
    setGoogleCallbackPending(false);
    setMicrosoftCallbackPending(false);
    try {
      const destination = reason === "switch" ? "/?signin=1&switch=1" : "/?signin=1";
      window.history.replaceState(null, "", destination);
      if (broadcast) {
        const event = JSON.stringify({ type: "logout", reason, at: Date.now() });
        window.localStorage.setItem("cc.auth.event", event);
        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("cc-auth");
          channel.postMessage({ type: "logout", reason });
          channel.close();
        }
      }
    } catch { /* browser storage/history unavailable */ }
  }, []);

  const logout = useCallback(async () => {
    const outcome = await performLogoutSequence();
    applySignedOut("logout");
    return outcome;
  }, [applySignedOut]);

  const switchAccount = useCallback(async () => {
    const outcome = await performLogoutSequence();
    applySignedOut("switch");
    return outcome;
  }, [applySignedOut]);

  // Account deletion: server sessions are already revoked; clear local credentials and leave the shell in place.
  const signOutAfterAccountDeletion = useCallback(async () => {
    const outcome = await performAccountDeletionSignOut();
    applySignedOut("logout");
    return outcome;
  }, [applySignedOut]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "cc.auth.event" && event.newValue) applySignedOut("logout", false);
    };
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("cc-auth") : null;
    const onMessage = (event) => {
      if (event.data?.type === "logout") applySignedOut(event.data.reason || "logout", false);
    };
    window.addEventListener("storage", onStorage);
    if (channel) channel.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (channel) channel.close();
    };
  }, [applySignedOut]);

  const capSet = useMemo(
    () => new Set(user?.capabilities || []),
    [user],
  );

  const hasCapability = useCallback(
    (cap) => {
      if (!user) return false;
      if (!cap) return true;
      if (capSet.has("*")) return true;
      return capSet.has(cap);
    },
    [user, capSet],
  );

  const value = useMemo(
    () => ({
      loading, user, providers, signedOut, googleCallbackPending, microsoftCallbackPending,
      login, logout, switchAccount, signOutAfterAccountDeletion, hasCapability, refreshMe,
    }),
    [loading, user, providers, signedOut, googleCallbackPending, microsoftCallbackPending, login, logout, switchAccount, signOutAfterAccountDeletion, hasCapability, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Same context, but null outside <AuthProvider> (unit-rendered components). */
export function useAuthOptional() {
  return useContext(AuthContext);
}

/**
 * Component-level capability guard.
 * <RequireCap cap="governance:read"><GovernanceWorkspace /></RequireCap>
 */
export function RequireCap({ cap, fallback = null, children }) {
  const { hasCapability } = useAuth();
  return hasCapability(cap) ? children : fallback;
}

// Re-export for convenience
export { formatApiError };
